import { MouseEvent } from 'react'
import { Avatar, Typography, Card, Box, Stack, Button } from '@mui/material'
import {
    CardItem,
    CardImg,
    TypDescription,
} from './style'
import { ViewCounter } from '../../../ui-kit/ViewCounter'
import { useNavigate } from 'react-router'
import { routesProfile } from '../../../../consts'
import { formatNumber } from '../../../../common/FormatNumber'
import type { IGrowthToolListItem } from '../../../../api/types/growthTools'
import { resolveGrowthToolCommentsTotal } from '../../../../api/types/growthTools'
import { IconComments } from '../../../../icons/IconComments'
import { IconLike } from '../../../../icons/IconLike'
import { IconLikeFilled } from '../../../../icons/IconLikeFilled'
import { useGrowthToolEngagement } from '../../../../hooks/useGrowthToolEngagement'
import {
    getUserInitialsFromDisplayName,
    resolveUserAvatarUrl,
    resolveUserDisplayName,
} from '../../../../utils/deletedUserDisplay'
import { COLORS } from '../../../../styles/variables'
import modalEmitter from '../../../ModalComponent/modalEmitter'
import { GrowthToolLikesModalBody } from '../../../Modals/GrowthToolLikesModalBody'
import { toast } from 'react-toastify'
import { profileApi } from '../../../../api/profile'

interface GrowthToolItemProps {
    growthTool: IGrowthToolListItem
}

export const DesktopCard = ({ growthTool }: GrowthToolItemProps) => {
    const navigate = useNavigate()
    const { data: me } = profileApi.useGetProfileQuery()
    const {
        isAuthor,
        isFavorite,
        favoritesCount,
        isLiked,
        likesCount,
        toggleFavorite,
        toggleLike,
        isFavoriteLoading,
        isLikeLoading,
    } = useGrowthToolEngagement({ growthTool })

    const commentsCount = resolveGrowthToolCommentsTotal(growthTool.counters.comments)
    const authorName = resolveUserDisplayName(growthTool.author)
    const pathKey = growthTool.slug || String(growthTool.id)

    const navigateToGrowthTool = () => {
        navigate(`/${routesProfile.GROWTHTOOLS.path}/${pathKey}`)
    }
    const handleEdit = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        navigate(`/${routesProfile.GROWTHTOOLS.path}/${growthTool.id}/edit`)
    }
    const handleToggleFavorite = async (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        await toggleFavorite()
    }
    const handleStopNavigation = (e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
    }
    const handleToggleLike = async (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        e.stopPropagation()
        if (isLikeLoading) return
        if (!me?.id) {
            toast.info('Войдите, чтобы ставить лайки')
            return
        }
        await toggleLike()
    }
    const handleOpenLikes = (e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (likesCount <= 0) return
        modalEmitter.emitOpenModal({
            dialog: {
                title: 'Лайки',
                body: <GrowthToolLikesModalBody growthToolId={growthTool.id} />,
                modalToCentrPosition: true,
            },
        })
    }
    const handleOpenComments = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        e.stopPropagation()
        navigate(`/${routesProfile.GROWTHTOOLS.path}/${pathKey}`)
    }

    return (
        <Card variant="primaryCard" sx={CardItem} onClick={navigateToGrowthTool}>
            <Stack direction="row" width="100%" gap={3}>
                {growthTool.image ? (
                    <Box component="img" src={growthTool.image} alt="" sx={CardImg} />
                ) : (
                    <Box sx={CardImg} />
                )}
                <Stack direction="column" justifyContent="space-between" minWidth={0} width="100%" gap={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="h3" flex={1} minWidth={0} noWrap>
                            {growthTool.title}
                        </Typography>
                        {isAuthor ? (
                            <Button variant="follow" sx={{ ml: 1 }} onClick={handleEdit}>
                                Редактировать
                            </Button>
                        ) : (
                            <Button
                                variant="follow"
                                sx={{ ml: 1 }}
                                disabled={isFavoriteLoading}
                                onClick={handleToggleFavorite}
                            >
                                {isFavorite ? 'В избранном' : 'В избранное'}
                            </Button>
                        )}
                    </Stack>
                    <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                        <Avatar src={resolveUserAvatarUrl(growthTool.author)} sx={{ width: 24, height: 24 }}>
                            {getUserInitialsFromDisplayName(authorName)}
                        </Avatar>
                        <Typography variant="black-14-light" noWrap>
                            {authorName}
                        </Typography>
                    </Stack>
                    <Typography variant="body1" sx={TypDescription}>
                        {growthTool.description}
                    </Typography>
                    <Stack direction="row" gap={1.5} alignItems="center">
                        <Typography variant="h4" sx={{ flex: 1 }}>
                            В избранном: {formatNumber(favoritesCount)}
                        </Typography>
                        <ViewCounter count={growthTool.counters.views} />
                    </Stack>
                </Stack>
            </Stack>
            <Stack direction="row" gap={{ xs: 2, sm: 1 }} m="12px 0 0 144px">
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleToggleLike}
                    onPointerDown={handleStopNavigation}
                    disabled={isLikeLoading}
                    sx={{
                        maxHeight: 32,
                        minWidth: 44,
                        px: likesCount > 0 ? 1.25 : undefined,
                        ...(isLiked
                            ? {
                                  backgroundColor: COLORS.CATSKILL_WHITE,
                                  color: COLORS.BLACK,
                                  '&:hover': { backgroundColor: COLORS.LINK_WATER },
                                  '@media (hover: none)': {
                                      '&:hover': { backgroundColor: COLORS.CATSKILL_WHITE },
                                  },
                              }
                            : {}),
                    }}
                >
                    <Stack direction="row" alignItems="center" gap={1} sx={{ lineHeight: 1 }}>
                        {isLiked ? <IconLikeFilled fontSize="small" /> : <IconLike fontSize="small" />}
                        <span
                            onClick={handleOpenLikes}
                            onPointerDown={handleStopNavigation}
                            style={{ userSelect: 'none' }}
                        >
                            {formatNumber(likesCount)}
                        </span>
                    </Stack>
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<IconComments />}
                    onClick={handleOpenComments}
                >
                    {formatNumber(commentsCount)} Комментариев
                </Button>
            </Stack>
        </Card>
    )
}
